import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import {
  currentTenant,
  currentUser,
  dashboardStats,
  recentActivity,
  approvalTasks,
  placements,
  agentStatuses,
  agentSlug,
  type AgentStatus,
} from '@/lib/mocks'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PageHeader, StatTile, AgentBadge, ConfidencePill } from '@/lib/atsUi'
import { Tabs } from '@/components/ui/tabs'
import { Plus, ArrowRight, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const STATE_STYLES: Record<AgentStatus['state'], { dot: string; label: string; pulse: boolean }> = {
  running: { dot: 'bg-success', label: 'Running', pulse: true },
  attention: { dot: 'bg-warning', label: 'Needs you', pulse: false },
  idle: { dot: 'bg-muted-foreground/40', label: 'Idle', pulse: false },
}

function AgentStrip() {
  const activeCount = agentStatuses.filter((a) => a.state === 'running').length
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
        </span>
        <h2 className="text-sm font-medium text-muted-foreground">
          {activeCount} agent{activeCount === 1 ? '' : 's'} working right now
        </h2>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {agentStatuses.map((a) => {
          const s = STATE_STYLES[a.state]
          return (
            <Link
              key={a.name}
              to="/agents/$agent"
              params={{ agent: agentSlug(a.name) }}
              className={cn(
                'group flex min-w-[212px] shrink-0 items-start gap-2.5 rounded-lg border bg-card px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                a.state === 'attention' && 'border-warning/50',
              )}
            >
              <span className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', s.dot, s.pulse && 'animate-pulse')} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{a.name}</span>
                  <span className="text-[11px] text-muted-foreground">{s.label}</span>
                </div>
                <p className="truncate text-xs text-muted-foreground">{a.detail}</p>
              </div>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground" />
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function Dashboard() {
  const [tab, setTab] = useState<'needs' | 'activity'>('needs')
  const topApprovals = approvalTasks.slice(0, 4)
  const approvalMins = Math.max(1, Math.ceil(approvalTasks.length * 0.5))

  return (
    <DashboardLayout
      title="Dashboard"
      navSections={atsNav}
      tenant={currentTenant}
      user={currentUser}
    >
      <PageHeader
        title="Dashboard"
        subtitle="What needs you today — and what the agents did while you were away."
        action={
          <Button size="sm" asChild>
            <Link to="/jobs/new">
              <Plus className="h-4 w-4" />
              New job
            </Link>
          </Button>
        }
      />

      <div className="space-y-8">

        {/* Live agent status */}
        <AgentStrip />

        {/* 3 stat tiles — clickable summary → detail */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile label="Needs approval" value={dashboardStats.pendingApprovals} href="/inbox" />
          <StatTile label="Active candidates" value={dashboardStats.candidatesInPipeline} href="/candidates" />
          <StatTile label="Placements (90 days)" value={placements.length} href="/placements" />
        </div>

        {/* Needs you + Recent activity — tabbed */}
        <section>
          <Tabs
            items={[
              { value: 'needs', label: 'Needs you', count: approvalTasks.length },
              { value: 'activity', label: 'Recent activity' },
            ]}
            value={tab}
            onChange={(v) => setTab(v as 'needs' | 'activity')}
          />

          {tab === 'needs' && (
            <>
              <p className="mb-3 text-sm text-muted-foreground">
                Agents prepared {approvalTasks.length} item{approvalTasks.length === 1 ? '' : 's'} for you — about {approvalMins} min.
              </p>
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {topApprovals.map((task) => (
                      <div key={task.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
                        <div className="mt-0.5 shrink-0">
                          <AgentBadge agent={task.agent} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground leading-snug">{task.title}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground truncate">{task.summary}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <ConfidencePill score={task.confidence} />
                          <Button size="sm" variant="outline" asChild>
                            <Link to="/inbox">Review</Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <div className="mt-3 text-right">
                <Link
                  to="/inbox"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  View all approvals
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </>
          )}

          {tab === 'activity' && (
            <>
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {recentActivity.map((item) => (
                      <div key={item.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
                        <div className="mt-0.5 shrink-0">
                          <AgentBadge agent={item.agent} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-foreground leading-snug">{item.text}</p>
                          <p className="mt-1 text-xs text-muted-foreground">{item.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <div className="mt-3 text-right">
                <Link
                  to="/audit"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  View full log
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </>
          )}
        </section>

      </div>
    </DashboardLayout>
  )
}

export const Route = createFileRoute('/dashboard')({
  component: Dashboard,
})
